import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Stepper, Step, StepLabel, Alert } from '@mui/material';
import authService from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import SocialLoginButtons from './SocialLoginButtons';

const LoginForm = ({ onSwitchToSignup, onForgotPassword }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: ''
  });
  const [cognitoUser, setCognitoUser] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const steps = ['Enter Credentials', 'Verify MFA'];

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await authService.signIn(formData.email, formData.password);
      
      if (result.requiresMFA) {
        setCognitoUser(result.cognitoUser);
        setCurrentStep(1);
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      setError(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (!cognitoUser) {
        throw new Error('Authentication session lost. Please try logging in again.');
      }
      
      await authService.confirmMFA(cognitoUser, formData.mfaCode, 'SOFTWARE_TOKEN_MFA');
      window.location.href = '/';
    } catch (error) {
      if (error.message.includes('Invalid code')) {
        setError('Invalid authenticator code. Please check your app and try again.');
      } else if (error.message.includes('auth state')) {
        setError('Authentication session expired. Please sign in again.');
        setTimeout(() => {
          handleBackToCredentials();
        }, 2000);
      } else {
        setError(error.message || 'MFA verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError(''); // Clear error when user types
  };

  const handleBackToCredentials = () => {
    setCurrentStep(0);
    setCognitoUser(null);
    setFormData({ ...formData, mfaCode: '' });
    setError('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <img 
              src={`${process.env.PUBLIC_URL}/logo192.png`} 
              alt="Trading Journal Logo" 
              style={{ width: 64, height: 64, borderRadius: '12px' }}
            />
          </Box>
          <Typography variant="h5" sx={{ mb: 2 }}>Sign In</Typography>
          
          {currentStep === 1 && (
            <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStep === 0 && (
          <>
            <SocialLoginButtons 
              onLoading={setLoading} 
              onError={setError} 
            />
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter your credentials to sign in
            </Typography>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              sx={{ mb: 2 }}
              autoComplete="email"
              autoFocus
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              sx={{ mb: 3 }}
              autoComplete="current-password"
            />
            <Button
              onClick={handleLogin}
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !formData.email || !formData.password}
              sx={{ mb: 2 }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </>
        )}

        {currentStep === 1 && (
          <>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter the 6-digit code from your authenticator app
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Signed in as: <strong>{formData.email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Authenticator Code"
              placeholder="000000"
              value={formData.mfaCode}
              onChange={(e) => handleInputChange('mfaCode', e.target.value.replace(/\D/g, ''))}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
              helperText="Enter the 6-digit code from your authenticator app"
              autoFocus
            />
            <Button
              onClick={handleMFAVerification}
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !formData.mfaCode || formData.mfaCode.length !== 6}
              sx={{ mb: 2 }}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <Button
              onClick={handleBackToCredentials}
              variant="text"
              fullWidth
              size="small"
            >
              Back to Credentials
            </Button>
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{' '}
            <Button variant="text" onClick={onSwitchToSignup}>
              Sign Up
            </Button>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Forgot your password?{' '}
            <Button variant="text" onClick={onForgotPassword}>
              Reset Password
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginForm;